import { assign, createMachine, interpret, Interpreter, State } from 'xstate-v4'
import type { Logger } from '../../../composables/useGtmLogRequest'
import { logger } from '../../../utility/logger'
import { getDefaultSearchFilters } from '../../search/defaults'
import { FetchCampsiteMapSearchService } from '../infrastructure/FetchCampsiteMapSearchService'
import { FetchCampsitePriceMapSearchService } from '../infrastructure/FetchCampsitePriceMapSearchService'
import type {
  CampsiteMapPoint,
  MapSearchCampsitesResults,
  MapSearchContext,
  SearchFilters,
} from '../schemas'
import { generateSnappedGrids, Grid } from './grids'

const PAGE_SIZE = 20
const LARGE_LIMIT = 999999
const USE_GRID_FOR_PRICE_SEARCH = true

interface MapSearchStateSchema {
  states: {
    idle: object
    searching: {
      states: {
        setting: object
        fetching: object
      }
    }
    results: object
    error: object
  }
}

interface GridResult {
  grid: Grid
  campsites: CampsiteMapPoint[]
}

interface ExtendedMapSearchContext extends MapSearchContext {
  searchedGrids: { [key: string]: GridResult }
  keyedResults: { [key: string]: CampsiteMapPoint[] }
  filtersKey: string
  signal?: AbortSignal
}

export type MapSearchStateEvent =
  | {
      type: 'search'
      filters: SearchFilters
      signal?: AbortSignal
      onlyBoundsChanged?: boolean
    }
  | {
      type: 'priceSearch'
      filters: SearchFilters
      signal?: AbortSignal
      onlyBoundsChanged?: boolean
    }
  | { type: 'RESULT_RECEIVED'; result: CampsiteMapPoint[] }
  | { type: 'SEARCH_FAILED' }

type SearchCallback = (event: MapSearchStateEvent) => void

interface QueueItem {
  grid: Grid | null
  gridFilters: SearchFilters
  context: ExtendedMapSearchContext
  callback: SearchCallback
  gridKey: string | null
}

export function returnMapSearchMachine() {
  return createMachine<ExtendedMapSearchContext, MapSearchStateEvent>(
    {
      id: 'search',
      initial: 'idle',
      context: {
        filters: getDefaultSearchFilters(),
        page: 1,
        languageCode: 'en-gb',
        logger: undefined,
        campsites: [],
        keyedResults: {},
        searchedGrids: {},
        filtersKey: '',
        errorOccurred: false,
        within: undefined,
      },
      on: {
        search: 'searching',
        priceSearch: 'searching',
        SEARCH_FAILED: {
          actions: assign({
            errorOccurred: (_context, _event) => true,
          }),
        },
        RESULT_RECEIVED: {
          actions: assign((context, event) => {
            const incomingCampsites = event.result
            // Note: returning `context` from XState v4 assign still triggers a
            // state transition and Vue reactivity. These early returns reduce
            // work inside the handler but do not prevent subscriber notifications.
            if (incomingCampsites.length === 0) {
              return context
            }

            const key = context.filtersKey
            const existingCampsites = context.campsites ?? []

            // Use Set for O(1) ID lookup instead of rebuilding Map each time
            const existingIds = new Set(
              existingCampsites.map((campsite) => campsite.id),
            )
            const newCampsites = incomingCampsites.filter(
              (campsite) => !existingIds.has(campsite.id),
            )

            if (newCampsites.length === 0) {
              return context
            }

            const updatedCampsites = existingCampsites.concat(newCampsites)
            const bounds = context.filters.bounds
            const filteredCampsites = bounds
              ? updatedCampsites.filter((campsite) => {
                  return (
                    campsite.lat <= bounds.northEast.lat &&
                    campsite.lat >= bounds.southWest.lat &&
                    campsite.lng <= bounds.northEast.lng &&
                    campsite.lng >= bounds.southWest.lng
                  )
                })
              : updatedCampsites

            return {
              ...context,
              campsites: filteredCampsites,
              keyedResults: key
                ? { ...context.keyedResults, [key]: updatedCampsites }
                : context.keyedResults,
            }
          }),
        },
      },
      states: {
        idle: {},
        searching: {
          initial: 'setting',
          states: {
            setting: {
              entry: assign({
                filters: (
                  _: ExtendedMapSearchContext,
                  event: MapSearchStateEvent,
                ) => {
                  if (event.type === 'search' || event.type === 'priceSearch') {
                    return event.filters
                  }
                  return undefined as unknown as SearchFilters
                },
                results: () => undefined,
                campsites: (
                  context: ExtendedMapSearchContext,
                  event: MapSearchStateEvent,
                ) => {
                  if (
                    !(event.type === 'search' || event.type === 'priceSearch')
                  ) {
                    return []
                  }
                  const bounds = event.filters.bounds
                  if (event.onlyBoundsChanged && bounds) {
                    logger('onlyBoundsChanged')
                    return (context.campsites ?? []).filter((campsite) => {
                      return (
                        campsite.lat <= bounds.northEast.lat &&
                        campsite.lat >= bounds.southWest.lat &&
                        campsite.lng <= bounds.northEast.lng &&
                        campsite.lng >= bounds.southWest.lng
                      )
                    })
                  } else {
                    return []
                  }
                },
                page: () => 1,
                errorOccurred: () => false,

                signal: (
                  _: ExtendedMapSearchContext,
                  event: MapSearchStateEvent,
                ) =>
                  event.type === 'search' || event.type === 'priceSearch'
                    ? event.signal
                    : undefined,

                filtersKey: (
                  _: ExtendedMapSearchContext,
                  event: MapSearchStateEvent,
                ) =>
                  event.type === 'search' || event.type === 'priceSearch'
                    ? JSON.stringify({
                        ...event.filters,
                        bounds: undefined,
                        base: undefined,
                        cleared: undefined,
                        limit: undefined,
                        page: undefined,
                        within: undefined,
                      })
                    : '',
              }),
              always: 'fetching',
            },
            fetching: {
              invoke: {
                src: (context) => async (callback: SearchCallback) => {
                  const { filters, logger } = context
                  const languageCode = context.languageCode ?? 'en-gb'
                  const signal = context.signal
                  if (!signal) {
                    return Promise.resolve()
                  }

                  if (signal.aborted) {
                    return Promise.resolve()
                  }
                  const isDatedSearch = Boolean(filters.dates)
                  const performSearch = async (
                    searchFilters: SearchFilters,
                    gridKey?: string,
                  ): Promise<MapSearchCampsitesResults | undefined> => {
                    if (gridKey && context.searchedGrids[gridKey]) {
                      return Promise.resolve(
                        context.searchedGrids[
                          gridKey
                        ] as unknown as MapSearchCampsitesResults,
                      )
                    }

                    if (isDatedSearch) {
                      const searchService =
                        new FetchCampsitePriceMapSearchService()

                      return await searchService.search(
                        searchFilters,
                        languageCode,
                        logger,
                        { timeout: 30000, signal },
                      )
                    } else {
                      const searchService = new FetchCampsiteMapSearchService()

                      return await searchService.search(
                        searchFilters,
                        languageCode,
                        logger,
                        { timeout: 30000, signal },
                      )
                    }
                  }

                  const searchManager = createLimitedConcurrencySearch(
                    8,
                    performSearch,
                    signal,
                  )

                  const useGrids =
                    filters.bounds &&
                    (!isDatedSearch || USE_GRID_FOR_PRICE_SEARCH)

                  if (useGrids) {
                    const grids = generateSnappedGrids(filters.bounds!)
                    grids.forEach((grid) => {
                      const gridKey = `${grid.bounds.southWest.lat},${grid.bounds.southWest.lng},${grid.bounds.northEast.lat},${grid.bounds.northEast.lng}:${context.filtersKey}`
                      const gridFilters = {
                        ...filters,
                        bounds: grid.bounds,
                        limit: LARGE_LIMIT,
                      }
                      searchManager.queue.push({
                        grid,
                        gridFilters,
                        context: context,
                        callback,
                        gridKey,
                      })
                    })
                    await searchManager.start()
                  } else {
                    // no bounds — paginate progressively so markers appear
                    // incrementally; toggle controls price search too
                    const shouldPaginate =
                      !isDatedSearch || USE_GRID_FOR_PRICE_SEARCH
                    const initialResult = await performSearch({
                      ...filters,
                      limit: shouldPaginate ? PAGE_SIZE : LARGE_LIMIT,
                      page: 1,
                    })

                    if (shouldPaginate) {
                      const totalCount = initialResult?.count ?? 0
                      const totalPages = Math.ceil(totalCount / PAGE_SIZE)
                      for (let page = 2; page <= totalPages; page++) {
                        const pageFilters = {
                          ...filters,
                          limit: PAGE_SIZE,
                          page,
                        }
                        searchManager.queue.push({
                          grid: null,
                          gridFilters: pageFilters,
                          context: context,
                          callback,
                          gridKey: null,
                        })
                      }
                    }
                    callback({
                      type: 'RESULT_RECEIVED',
                      result: initialResult?.campsites ?? [],
                    })
                    if (shouldPaginate) {
                      await searchManager.start()
                    }
                    // this will contain meta data like the point

                    return initialResult
                  }
                },
                onDone: [
                  {
                    target: '#search.results',
                    actions: ['setResults'],
                    cond: (_context, event) => !!event.data,
                  },
                  { target: '#search.results' },
                ],
                onError: [
                  {
                    target: '#search.idle',
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- XState v4 error event typing
                    cond: (_context, event) => event.data.cause?.code === 20,
                  },
                  { target: '#search.error' },
                ],
              },
            },
          },
        },
        results: {},
        error: {
          onEntry: ['setError'],
          onExit: ['clearError'],
        },
      },
    },
    {
      actions: {
        clearError: assign((_) => {
          return {
            errorMessage: undefined,
            errorOccurred: false,
          }
        }),

        setError: assign((_, { data }: any) => {
          return {
            errorMessage: data.toString(),
            errorOccurred: true,
          }
        }),

        setResults: assign((_, { data }: any) => {
          return {
            results: data,
            within: data?.within,
          }
        }),

        logError: (context, event) => {
          console.error(
            'MapsError',
            JSON.stringify(event),
            JSON.stringify(context),
          )
        },
      },
      services: {
        // No additional services defined here as the logic is handled within the states
      },
    },
  )
}

function createLimitedConcurrencySearch(
  concurrencyLimit: number,
  performSearch: (
    filters: SearchFilters,
    gridKey?: string,
  ) => Promise<MapSearchCampsitesResults | undefined>,
  signal: AbortSignal,
) {
  let active = 0
  const queue: QueueItem[] = []
  let resolveAll: () => void
  const allTasksDone = new Promise<void>((resolve) => {
    resolveAll = resolve
  })

  // Batch RESULT_RECEIVED events: buffer incoming campsites and flush once
  // per animation frame instead of firing per grid result. Results still
  // appear progressively (each frame flushes what arrived), but concurrent
  // grid responses within the same ~16ms frame coalesce into a single
  // XState transition instead of 4-8 separate ones.
  let pendingCampsites: CampsiteMapPoint[] = []
  let flushScheduled = false
  let lastCallback: SearchCallback | null = null

  const flushResults = () => {
    flushScheduled = false
    if (signal.aborted || pendingCampsites.length === 0 || !lastCallback) {
      pendingCampsites = []
      return
    }
    const batch = pendingCampsites
    pendingCampsites = []
    lastCallback({ type: 'RESULT_RECEIVED', result: batch })
  }

  const bufferResult = (
    campsites: CampsiteMapPoint[],
    callback: SearchCallback,
  ) => {
    if (campsites.length === 0) {
      return
    }
    pendingCampsites.push(...campsites)
    lastCallback = callback
    if (!flushScheduled) {
      flushScheduled = true
      requestAnimationFrame(flushResults)
    }
  }

  const runNext = () => {
    if (signal.aborted) {
      queue.length = 0
      active = 0
    }
    if (queue.length === 0 && active === 0) {
      // Flush any buffered results synchronously before resolving,
      // otherwise the last batch is lost when XState transitions away
      // from the searching state before the rAF fires.
      flushResults()
      resolveAll()
      return
    }
    if (queue.length === 0 || active >= concurrencyLimit) {
      return
    }
    const item = queue.shift()!
    const { grid, gridFilters, context, callback, gridKey } = item
    active++
    performSearch(gridFilters, gridKey ?? undefined)
      .then((result) => {
        if (signal.aborted) {
          return
        }

        const campsites = result?.campsites ?? []
        if (grid && gridKey) {
          context.searchedGrids[gridKey] = { grid, campsites }
        }

        bufferResult(campsites, callback)
      })
      .catch((error: Error & { cause?: { name?: string } }) => {
        if (error.name === 'AbortError' || error.cause?.name === 'AbortError') {
          return
        }

        callback({ type: 'SEARCH_FAILED' })
      })
      .finally(() => {
        active--
        requestIdleCallback(() => runNext())
      })
    requestIdleCallback(() => runNext())
  }
  const start = () => {
    runNext()
    return allTasksDone
  }

  return { queue, runNext, start }
}

export type MapSearchMachineInterpreter = Interpreter<
  ExtendedMapSearchContext,
  MapSearchStateSchema,
  MapSearchStateEvent
>

export type MapSearchState = State<
  ExtendedMapSearchContext,
  MapSearchStateEvent
>

export function getMapSearchMachineInterpreted(
  languageCode: string,
  logger: Logger,
) {
  const machine = returnMapSearchMachine()
  return interpret(
    machine.withContext({
      ...machine.context,
      languageCode,
      logger,
    }),
  )
}
